import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { BannerModal } from "./BannerCard";

export const bannerKey = (banner) => `${banner.id}:${banner.revision}:${banner.occurrence}`;

export default function AppBannerManager({ enabled, companyId }) {
  const location = useLocation();
  const [current, setCurrent] = useState(null);
  const dismissed = useRef(new Set());
  const scope = useRef(companyId);
  const blockedRoute = /^\/(admin|login|setup|ispettore|privacy|termini|cookie-policy|security)(\/|$)/.test(location.pathname);
  const active = enabled && !blockedRoute;

  useEffect(() => {
    setCurrent(null);
    dismissed.current.clear();
    scope.current = companyId;
  }, [companyId, enabled]);

  useEffect(() => {
    if (!active) { setCurrent(null); return undefined; }
    let alive = true, busy = false;
    const controller = new AbortController();
    const poll = async () => {
      if (busy || document.visibilityState === "hidden") return;
      busy = true;
      try {
        const response = await fetch("/api/app-banners/status.php", { credentials: "include", cache: "no-store", signal: controller.signal });
        const data = await response.json();
        if (!alive || !response.ok || !Array.isArray(data.banners)) return;
        // Let other app dialogs finish before introducing a communication.
        setCurrent((previous) => {
          if (previous && data.banners.some((item) => bannerKey(item) === bannerKey(previous))) return previous;
          if (document.querySelector('[role="dialog"][aria-modal="true"]:not(.nb-overlay)')) return null;
          return data.banners.find((item) => !dismissed.current.has(bannerKey(item))) || null;
        });
      } catch { /* Communication failures do not interrupt the app. */ }
      finally { busy = false; }
    };
    poll();
    const timer = setInterval(poll, 15000);
    window.addEventListener("focus", poll);
    document.addEventListener("visibilitychange", poll);
    return () => { alive = false; controller.abort(); clearInterval(timer); window.removeEventListener("focus", poll); document.removeEventListener("visibilitychange", poll); };
  }, [active, companyId]);

  function close() {
    if (!current) return;
    dismissed.current.add(bannerKey(current));
    fetch("/api/app-banners/seen.php", {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: current.id, revision: current.revision, occurrence: current.occurrence }),
    }).catch(() => {});
    setCurrent(null);
  }
  if (!active || scope.current !== companyId || !current) return null;
  return <BannerModal key={bannerKey(current)} banner={current} onClose={close} />;
}
